import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { Config, IS_INTEGRATED_VERSION } from '../../config/ConfigManager'
import { useSearchStore } from '../../store/search/SearchStore'
import { useUserStore } from '../../store/user/UserStore'
import { useScrollToTop } from '../useScrollToTop'
import { EmbeddedCommunicationsManager } from '../../api/embedded/EmbeddedCommunicationsManager'
import { observer } from 'mobx-react-lite'
import NiceModal from '@ebay/nice-modal-react'
import { AppLoader } from './AppLoader'
import { RootState } from 'store'
import { useSelector } from 'react-redux'
import { useUpdateCheck } from '../../hooks/useUpdateCheck'

/**
 * The root layout of the app.
 * This just redirects to appropriate page as the app doesn't have an index route.
 */
export const RootLayout = observer(() => {
  const userStore = useUserStore()
  const searchStore = useSearchStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isCartInitialized = useSelector(
    (state: RootState) => state.cart.isInitialized
  )

  // Used to auto scroll the page upon navigation.
  useScrollToTop()

  // Hook used to check for updates and refresh the page.
  useUpdateCheck()

  const appHasLoaded = useMemo(
    () =>
      Config.isNotCartOnlyMode
        ? userStore.userHasLoaded
        : isCartInitialized && userStore.userHasLoaded,
    [isCartInitialized, userStore.userHasLoaded]
  )

  // TODO: Look into replacing useCallback with useMemo.
  const landingPage = useMemo((): string => {
    if (IS_INTEGRATED_VERSION) {
      // The order of the conditions matter here.
      if (!Config.isNotCartOnlyMode) {
        return '/cart'
      }
      if (EmbeddedCommunicationsManager.isAstPunchOut) {
        return '/astPartsCatalog'
      }

      if (searchStore.currentVehicle && searchStore.currentVehicle.engine?.id) {
        return '/partsCatalog'
      }
    }
    return '/home'
  }, [searchStore.currentVehicle])

  useEffect(() => {
    // To prevent any unexpected infinite loop. (Mainly from /home).
    if (location.pathname === landingPage) {
      return
    }
    // Redirect to proper page when user tries to access / or /home.
    if (
      appHasLoaded &&
      (location.pathname === '/' || location.pathname === '/home')
    ) {
      if (userStore.preferences.userId) {
        navigate(landingPage)
      } else {
        navigate('/login')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appHasLoaded, navigate])

  return appHasLoaded ? (
    <NiceModal.Provider>
      <Outlet />
    </NiceModal.Provider>
  ) : (
    <AppLoader />
  )
})
